import React, { useContext, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { UrlContext } from "../../contexts/UrlContext";
import { motion, AnimatePresence } from "framer-motion";
import { DateTime } from "luxon";
import styles from "./MyProjectPage.module.css";
import ViewApplication from "./ViewApplication";
import Application from "./Application";

export default function ProjectsPage({ expertID, showStickyHeader }) {
  const { API_URL } = useContext(UrlContext);
  const mediaLink = "https://media.licdn.com/";
  const databaseLink = "https://images.expertpowerhouse.com";
  const [publishedProjects, setPublishedProjects] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showApplication, setShowApplication] = useState(false);
  const [viewApplication, setViewApplication] = useState(false);

  useEffect(() => {
    fetchProjects(page);
  }, [page]);

  const fetchProjects = async (pageNumber) => {
    const token = localStorage.getItem("AccessToken");
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}get-my-projects?page=${pageNumber}&per_page=10&expertID=${expertID}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        Swal.fire({
          title: "Something Went Wrong!",
          icon: "error",
          confirmButtonColor: "#365561"
        });
      }

      const data = await response.json();

      if (data.status === "success") {
        setPublishedProjects(data.projects);
        setTotalPages(data.pages);
      } else {
        Swal.fire({
          title: "Something Went Wrong!",
          icon: "error",
          confirmButtonColor: "#365561"
        });
      }
    } catch (error) {
      Swal.fire({ title: "Something Went Wrong!", icon: "error", confirmButtonColor: "#365561" });
    } finally {
      setLoading(false);
    }
  };

  const getPaginationNumbers = (page, totalPages, maxButtons) => {
    const pages = [];

    if (totalPages <= maxButtons + 1) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      const leftSide = Math.floor(maxButtons / 2);
      const rightSide = maxButtons - leftSide - 1;

      let start = Math.max(2, page - leftSide);
      let end = Math.min(totalPages - 1, page + rightSide);

      if (page <= leftSide) {
        start = 2;
        end = maxButtons;
      }

      if (page + rightSide >= totalPages) {
        start = totalPages - maxButtons + 1;
        end = totalPages - 1;
      }

      pages.push(1);
      if (start > 2) pages.push("...");
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  useEffect(() => {
    const updateView = () => setIsMobile(window.innerWidth < 640);
    updateView();
    window.addEventListener("resize", updateView);
    return () => window.removeEventListener("resize", updateView);
  }, []);

  const paginationNumbers = getPaginationNumbers(
    page,
    totalPages,
    isMobile ? 3 : 10
  );

  const formatRelativeDate = (dateStr) => {
    const created = DateTime.fromRFC2822(dateStr);
    const now = DateTime.local();

    if (!created.isValid) return "Unknown date";

    if (created.hasSame(now, "day")) return "Today";

    const diffInDays = Math.round(now.diff(created, "days").days);

    if (diffInDays >= 365) {
      const years = Math.floor(diffInDays / 365);
      return `${years} year${years > 1 ? "s" : ""} ago`;
    } else if (diffInDays >= 30) {
      const months = Math.floor(diffInDays / 30);
      return `${months} month${months > 1 ? "s" : ""} ago`;
    } else {
      return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
    }
  };

  const formatStartDate = (dateStr) => {
    const date = DateTime.fromRFC2822(dateStr); // or .fromISO() if your format changes
    if (!date.isValid) return "Start date unknown";

    return `Starts from ${date.toFormat("LLLL d, yyyy")}`;
  };

  const getEmploymentMode = (type) => {
    if (!type) return "N/A";

    const lower = type.toLowerCase();

    const hasRemote = lower.includes("remote");
    const hasOnsite = lower.includes("onsite");

    if (hasRemote && hasOnsite) return "Hybrid";
    if (hasRemote) return "Remote";
    if (hasOnsite) return "On-Site";

    return "N/A";
  };

  const formatSkills = (skillsString) => {
    if (!skillsString) return "N/A";
    return skillsString
      .split(",")
      .map((skill) => skill.trim())
      .filter(Boolean) // removes empty strings
      .join(", ");
  };

  const stripHtml = (html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  const getProjectStatus = (startDateStr, endDateStr) => {
    const now = DateTime.now();

    if (!startDateStr || !endDateStr) {
      return {
        label: "Date unavailable",
        style: "bg-gray-100 text-gray-800",
      };
    }

    const startDate = DateTime.fromRFC2822(startDateStr);
    const endDate = DateTime.fromRFC2822(endDateStr);

    if (!startDate.isValid || !endDate.isValid) {
      return {
        label: "Date error",
        style: "bg-gray-100 text-gray-800",
      };
    }

    if (now < startDate) {
      return {
        label: `Starts from ${startDate.toFormat("LLLL d, yyyy")}`,
        style: "bg-yellow-100 text-yellow-800",
      };
    }

    if (now >= startDate && now <= endDate) {
      return {
        label: "On-going project",
        style: "bg-blue-100 text-blue-800",
      };
    }

    return {
      label: "Project closed",
      style: "bg-green-100 text-green-800",
    };
  };

  const getStatusStyle = (status) => {
    const normalizedStatus = status.startsWith("Project Closed")
      ? "Project Closed"
      : status;

    const statusStyles = {
      "Not Started": "bg-gray-200 text-gray-800",
      Profiling: "bg-blue-100 text-blue-800",
      "Waiting for input": "bg-yellow-100 text-yellow-800",
      Contracting: "bg-indigo-100 text-indigo-800",
      "Project Closed": "bg-red-100 text-red-800",
    };

    return statusStyles[normalizedStatus] || "bg-gray-100 text-gray-800";
  };

  const getOrdinal = (n) => {
    const s = ["th", "st", "nd", "rd"],
      v = n % 100;
    return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
  };

  const handleExpertRate = (ext) => {
    let formattedRate = ext.expertRate;
    const rateParts = ext.expertRate?.split(" ");
    if (rateParts && rateParts.length === 2 && !isNaN(rateParts[0])) {
      const [rawRate, rawCurrency] = rateParts;
      const frequency = ext.cons_exp === "exp" ? "per hour" : "per day";
      formattedRate =
        ext.cons_exp === "exp"
          ? `${rawCurrency} ${rawRate}`
          : `${rawCurrency} ${rawRate} ${frequency}`;
    }
    return formattedRate;
  };

  const updateProjectStatus = (projectId, newStatus) => {
    setPublishedProjects((prevProjects) =>
      prevProjects.map((project) =>
        project.id === projectId
          ? { ...project, requestStatus: newStatus }
          : project
      )
    );

    if (selectedProject?.id === projectId) {
      setSelectedProject((prev) => ({ ...prev, requestStatus: newStatus }));
    }
  };

  return (
    <>
      <section className="col-span-4 space-y-6 relative min-h-[300px]">
        <motion.div className="flex flex-col lg:flex-row gap-4 transition-all duration-500">
          {/* Project List */}
          <motion.div
            // layout
            className={`transition-all duration-500 ${
              selectedProject ? "hidden lg:block lg:w-1/2" : "block lg:w-full"
            }`}
          >
            <div className="space-y-3 min-h-[120px] flex flex-col">
              {/* Overlay Spinner */}
              {loading && (
                <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-white/30 rounded-xl">
                  <svg
                    className="animate-spin h-20 w-20 text-gray-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="#365561"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="#365561"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                </div>
              )}
              {/* Project List or Empty State */}
              {publishedProjects.length === 0 && !loading ? (
                <p className="text-gray-500 text-center text-sm">
                  You don't have any project!
                </p>
              ) : (
                publishedProjects.map((project, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      setSelectedProject(project);
                    }}
                    className={`relative group rounded-xl shadow p-3 cursor-pointer transition-all duration-200 ${
                      project.id === selectedProject?.id
                        ? "bg-[#eaf2f8] border-l-4 border-[#365561]"
                        : "bg-white hover:bg-gray-50"
                    }`}
                  >
                    <div className="relative inline-block mb-1">
                      <h2 className="text-xl font-bold">{project.name}</h2>
                    </div>
                    <p className="text-sm text-gray-500">
                      {project.country
                        ? `${project.country}`
                        : project.region
                        ? `${project.region}`
                        : null}{" "}
                      {project.employmentType
                        ? ` • ${getEmploymentMode(project.employmentType)}`
                        : null}{" "}
                      •{" "}
                      {project.projStartDate
                        ? formatStartDate(project.projStartDate)
                        : null}
                    </p>
                    <p className="text-sm text-gray-500">
                      {project.languagesNames.length > 0
                        ? project.languagesNames.join(", ")
                        : "No language mentioned"}{" "}
                      {project.projectDuration
                        ? ` • ${project.projectDuration}`
                        : ` • No duration mentioned`}
                    </p>
                    {!project.published ? (
                      <p className="text-xs text-gray-400 mt-1">
                        This project hasn't been published online.!
                      </p>
                    ) : (
                      <p
                        className="text-xs text-gray-400 mt-1 overflow-hidden"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {stripHtml(
                          project.description || "No description available."
                        )}
                      </p>
                    )}
                    <div className="mt-3 flex justify-between items-center text-xs text-gray-600 flex-wrap gap-y-1">
                      {/* Left side: Contact EPH button */}
                      <div className="flex items-center gap-4">
                        {project.expertStatus === "Applied" ? (
                          ["Draft"].includes(project.requestStatus) ? (
                            <span className="px-3 py-1 rounded-full text-sm font-medium shadow bg-gray-100">
                              Click to resume application
                            </span>
                          ) : ["Waiting for review"].includes(project.requestStatus) && project.status.startsWith("Project Closed") ? (
                            <span className="px-3 py-1 rounded-full text-sm font-medium shadow bg-red-100 text-red-800">
                              Project Closed
                            </span>
                          ) : ["Rejected"].includes(project.requestStatus) ? (
                            <span className="px-3 py-1 rounded-full text-sm font-medium shadow bg-red-100 text-red-800">
                              Project Closed
                            </span>
                          ) : ["Waiting for review"].includes(project.requestStatus) ? (
                            <span className="px-3 py-1 rounded-full text-sm font-medium shadow bg-gray-100 text-gray-800">
                              Application in Review
                            </span>
                          ) :  project.expertStatus === "Participated" ? (
                            (() => {
                              const { label, style } = getProjectStatus(
                                project.startDate,
                                project.endDate
                              );
                              return (
                                <span
                                  className={`px-3 py-1 rounded-full text-sm font-medium shadow ${style}`}
                                >
                                  {label}
                                </span>
                              );
                            })()
                          ) : [
                              "Profiling",
                              "Waiting for input",
                              "Contracting",
                            ].includes(project.status) ? (
                            <span className="px-3 py-1 rounded-full text-sm font-medium shadow bg-yellow-100 text-yellow-800">
                              Waiting for Client Input
                            </span>
                          ) : (
                            <span
                              className={`px-3 py-1 rounded-full text-sm font-medium shadow ${getStatusStyle(
                                project.status
                              )}`}
                            >
                              {project.status.startsWith("Project Closed")
                                ? "Project Closed"
                                : project.status}
                            </span>
                          )
                        ) : project.expertStatus === "Participated" ? (
                          (() => {
                            const { label, style } = getProjectStatus(
                              project.startDate,
                              project.endDate
                            );
                            return (
                              <span
                                className={`px-3 py-1 rounded-full text-sm font-medium shadow ${style}`}
                              >
                                {label}
                              </span>
                            );
                          })()
                        ) : [
                            "Profiling",
                            "Waiting for input",
                            "Contracting",
                          ].includes(project.status) ? (
                          <span className="px-3 py-1 rounded-full text-sm font-medium shadow bg-yellow-100 text-yellow-800">
                            Waiting for Client Input
                          </span>
                        ) : (
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-medium shadow ${getStatusStyle(
                              project.status
                            )}`}
                          >
                            {project.status.startsWith("Project Closed")
                              ? "Project Closed"
                              : project.status}
                          </span>
                        )}
                      </div>

                      {/* Right side: Deadline and Published Date */}
                      <div className="flex items-center gap-4">
                        {project.expertStatus === "Participated" ? (
                          <div className="relative inline-block">
                            <span className="bg-green-600 text-white text-xs px-2.5 py-1 rounded-full font-semibold shadow">
                              Participated
                            </span>
                          </div>
                        ) : (
                          <p className="font-semibold font-italic text-gray-500">
                            You were interested in this project!
                          </p>
                        )}
                      </div>
                    </div>
                    {project.id === selectedProject?.id ? null : (
                      <div className="absolute top-0 right-0 z-10 group-hover:opacity-100 opacity-0 transition-opacity duration-300 pointer-events-none">
                        <div
                          className="relative w-[75px] h-[75px] bg-[#365561]"
                          style={{
                            clipPath: "polygon(100% 0, 0 0, 100% 100%)",
                            borderTopRightRadius: "0.75rem",
                          }}
                        >
                          {/* Text layer on top of triangle */}
                          <div className="absolute top-0 left-0 w-full h-full flex items-start justify-end pr-1 pt-1 pointer-events-none">
                            <p className="text-[10px] text-white font-semibold text-right leading-tight">
                              Click to see
                              <br />
                              more...
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            {/* {publishedProjects.length > 0 && ( */}
            {totalPages > 1 && (
              <div className="mt-6 flex flex-wrap items-center gap-[6px]">
                <div className="mt-4 flex flex-wrap items-center gap-[6px]">
                  <div className="relative group">
                    <button
                      onClick={() => setPage(1)}
                      disabled={page === 1}
                      className={`w-8 h-8 flex items-center justify-center border rounded-full text-sm focus:outline-none ${
                        page === 1
                          ? "bg-gray-200 text-gray-500"
                          : "bg-white text-black"
                      }`}
                      aria-label="First page"
                    >
                      <i className="fa-solid fa-angles-left" />
                    </button>
                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                      First Page
                    </span>
                  </div>

                  <div className="relative group">
                    <button
                      onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                      disabled={page === 1}
                      className={`w-8 h-8 flex items-center justify-center border rounded-full text-sm focus:outline-none ${
                        page === 1
                          ? "bg-gray-200 text-gray-500"
                          : "bg-white text-black"
                      }`}
                      aria-label="Previous page"
                    >
                      <i className="fa-solid fa-angle-left" />
                    </button>
                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                      Previous Page
                    </span>
                  </div>

                  {/* Page Numbers */}
                  {paginationNumbers.map((p, i) => (
                    <button
                      key={i}
                      disabled={p === "..."}
                      onClick={() => typeof p === "number" && setPage(p)}
                      className={`w-8 h-8 flex items-center justify-center border rounded-full text-sm focus:outline-none ${
                        page === p
                          ? "bg-[#365561] text-white font-semibold"
                          : "bg-white text-black"
                      } ${p === "..." && "cursor-default opacity-60"}`}
                    >
                      {p === "..." ? "…" : p}
                    </button>
                  ))}

                  <div className="relative group">
                    <button
                      onClick={() =>
                        setPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={page === totalPages}
                      className={`w-8 h-8 flex items-center justify-center border rounded-full text-sm focus:outline-none ${
                        page === totalPages
                          ? "bg-gray-200 text-gray-500"
                          : "bg-white text-black"
                      }`}
                      aria-label="Next page"
                    >
                      <i className="fa-solid fa-angle-right" />
                    </button>
                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                      Next Page
                    </span>
                  </div>

                  <div className="relative group">
                    <button
                      onClick={() => setPage(totalPages)}
                      disabled={page === totalPages}
                      className={`w-8 h-8 flex items-center justify-center border rounded-full text-sm focus:outline-none ${
                        page === totalPages
                          ? "bg-gray-200 text-gray-500"
                          : "bg-white text-black"
                      }`}
                      aria-label="Last page"
                    >
                      <i className="fa-solid fa-angles-right" />
                    </button>
                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                      Last Page
                    </span>
                  </div>
                </div>
              </div>
            )}
          </motion.div>

          {/* Project Detail */}
          <AnimatePresence mode="wait">
            {selectedProject && (
              <motion.div
                key="detail-panel"
                layout
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                className={`
                  w-full lg:w-1/2 
                  lg:sticky ${
                    showStickyHeader ? "lg:top-[140px]" : "lg:top-[100px]"
                  }
                  lg:self-start
                  lg:max-h-[calc(100vh-160px)]
                  overflow-y-hidden hover:overflow-y-auto 
                  bg-white p-6 rounded-xl shadow-md 
                  scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent
                  ${styles.scrollbarOverlay}
                `}
              >
                <div className="flex justify-end mb-2">
                  <button
                    className="top-5 right-6 text-gray-500 hover:text-black hover:bg-gray-200 py-1 px-2.5 rounded-full transition focus:outline-none"
                    onClick={() => setSelectedProject(null)}
                    title="Close"
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                </div>
                <h2 className="text-xl font-bold">{selectedProject.name}</h2>
                <p className="text-sm text-gray-500 mt-3">
                  {selectedProject.country
                    ? selectedProject.country
                    : selectedProject.region
                    ? selectedProject.region
                    : "N/A"}
                  {" • "}
                  {formatRelativeDate(selectedProject.date_created)}
                  {selectedProject.projStartDate
                    ? ` •  ${formatStartDate(selectedProject.projStartDate)}`
                    : null}
                </p>
                <div className="mt-3 text-sm text-gray-700">
                  <p>
                    <span className="mr-2">💼</span>
                    {getEmploymentMode(selectedProject.employmentType)}{" "}
                    {selectedProject.projectDuration
                      ? ` •  ${selectedProject.projectDuration}`
                      : null}
                  </p>
                  <p>
                    <span className="mr-2">🏭</span>
                    {selectedProject.industryPref === "Yes"
                      ? selectedProject.industry || "N/A"
                      : "N/A"}
                  </p>
                  <div className="flex items-start gap-2">
                    <span className="mt-0.5">🛠️</span>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {formatSkills(selectedProject.skills)}
                    </p>
                  </div>
                </div>

                {selectedProject.expertStatus === "Participated" &&
                selectedProject.expertRate ? (
                  <div className="mt-3 p-2 text-sm border border-1 rounded font-italic text-gray-500">
                    <p className="text-lg font-semibold mb-2">
                      Project participation details:
                    </p>
                    <p className="font-semibold">
                      <span className="font-normal">Project type: </span>
                      {selectedProject.cons_exp === "exp"
                        ? "Expert Call"
                        : selectedProject.cons_exp === "con"
                        ? "Long term project"
                        : selectedProject.cons_exp === "int"
                        ? "Interim"
                        : "N/A"}
                    </p>
                    <p className="font-semibold">
                      <span className="font-normal">Project involvement: </span>
                      {selectedProject.cons_exp === "exp"
                        ? "One hour Expert-Call"
                        : selectedProject.startDate || selectedProject.endDate
                        ? `${DateTime.fromRFC2822(
                            selectedProject.startDate
                          ).toFormat("MMM dd, yyyy")} - ${DateTime.fromRFC2822(
                            selectedProject.endDate
                          ).toFormat("MMM dd, yyyy")}`
                        : "No date mentioned"}
                    </p>
                    <p className="font-semibold">
                      <span className="font-normal">Rate: </span>
                      {handleExpertRate(selectedProject)}
                    </p>
                    {selectedProject.cons_exp === "exp" && (
                      <p className="font-semibold">
                        <span className="font-normal">Call duration: </span>
                        {selectedProject.callDuration} minutes
                      </p>
                    )}

                    {/* Extensions */}
                    {selectedProject.extensions &&
                      selectedProject.extensions.length > 0 && (
                        <div className="mt-2">
                          <ul>
                            {selectedProject.extensions.map((ext, index) => {
                              let formattedRate = ext.expertRate;
                              const rateParts = ext.expertRate?.split(" ");
                              if (
                                rateParts &&
                                rateParts.length === 2 &&
                                !isNaN(rateParts[0])
                              ) {
                                const [rawRate, rawCurrency] = rateParts;
                                const frequency =
                                  ext.cons_exp === "exp" ? "per hour" : "per day";
                                formattedRate =
                                  ext.cons_exp === "exp"
                                    ? `${rawCurrency} ${rawRate}`
                                    : `${rawCurrency} ${rawRate} ${frequency}`;
                              }

                              return (
                                <li
                                  key={index}
                                  className="text-xs text-gray-500 mb-1"
                                >
                                  <p className="font-semibold">
                                    {getOrdinal(index + 1)} extension:
                                  </p>
                                  <p className="ml-2 font-semibold">
                                    <span className="font-normal">
                                      Project involvement:{" "}
                                    </span>
                                    {ext.cons_exp === "exp"
                                      ? "One hour Expert-Call"
                                      : ext.startDate || ext.endDate
                                      ? `${DateTime.fromRFC2822(
                                          ext.startDate
                                        ).toFormat(
                                          "MMM dd, yyyy"
                                        )} - ${DateTime.fromRFC2822(
                                          ext.endDate
                                        ).toFormat("MMM dd, yyyy")}`
                                      : "No date mentioned"}
                                  </p>
                                  <p className="ml-2 font-semibold">
                                    <span className="font-normal">Rate: </span>
                                    {formattedRate}
                                  </p>
                                  {ext.cons_exp === "exp" && (
                                    <p className="ml-2 font-semibold">
                                      <span className="font-normal">
                                        Call duration:{" "}
                                      </span>
                                      {ext.callDuration} minutes
                                    </p>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                  </div>
                ) : (
                  <>
                    <p className="mt-3 text-m font-semibold font-italic text-gray-500">
                      You were interested in this project!
                    </p>
                    
                    {(selectedProject?.isSelfApplied || selectedProject.requestStatus === "Submitted")&& (
                      <>
                        {selectedProject?.requestStatus === "Draft" ? (
                          <button className="px-3 py-2 mt-2 rounded-full text-sm font-medium bg-[#365561] text-white" onClick={() => setShowApplication(true)}>
                            Resume application
                          </button>
                        ) : (
                          <button className="px-3 py-2 mt-2 rounded-full text-sm font-medium bg-gray-200 text-gray-800" onClick={() => setViewApplication(true)}>
                            View Application
                          </button>
                        )}
                      </>
                    )}
                  </>
                )}

                <p className="mt-3 text-lg font-semibold">About the project</p>
                {!selectedProject.published ? (
                  <p className="mt-3 text-sm text-gray-500">
                    No project description available!
                  </p>
                ) : (
                  <div
                    className="mt-3 text-sm text-gray-700 leading-relaxed [&_ul]:list-disc [&_ul]:pl-6 [&_h3]:text-lg [&_h3]:font-semibold"
                    dangerouslySetInnerHTML={{
                      __html:
                        selectedProject.description ||
                        "<p>No description available.</p>",
                    }}
                  ></div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </section>

      {viewApplication && (
        <ViewApplication 
          projectID={selectedProject.id}
          expertID={expertID}
          onClose={() => setViewApplication(false)}
        />
      )}

      {showApplication && (
        <Application
          project={selectedProject}
          selfApplied={true}
          expertID={expertID}
          updateProjectStatus={updateProjectStatus}
          onClose={() => setShowApplication(false)}
          
        />
      )}
    </>
  );
}
